import { DocumentType } from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

import { DOCUMENT_TYPE_DISPLAY_NAMES } from '../documents/documents.constants';
import { HouseholdMissingDocumentFragment } from './graphql/MissingDocuments.generated';

export function is18OrOlder(dob?: Date | null | undefined) {
  if (!dob) {
    return false;
  }
  const today = new Date();
  const eighteenYearsAgo = new Date(
    today.getFullYear() - 18,
    today.getMonth(),
    today.getDate()
  );
  return dob <= eighteenYearsAgo;
}

export function getDocumentTypeDisplayName(documentType: DocumentType) {
  if (documentType === DocumentType.SignedTrustDocument) {
    return 'Revocable trust';
  }
  return DOCUMENT_TYPE_DISPLAY_NAMES[documentType];
}
export function getHouseholdMissingDocuments(
  missingDocuments: HouseholdMissingDocumentFragment[]
) {
  return missingDocuments.filter((d) => !d.clientProfile);
}

export function getPrimaryClientMissingDocuments(
  missingDocuments: HouseholdMissingDocumentFragment[]
) {
  const result: Record<string, HouseholdMissingDocumentFragment[]> = {};
  missingDocuments
    .filter((d) => d.clientProfile?.isPrimary)
    .forEach((d) => {
      if (d.clientProfile) {
        if (!result[d.clientProfile.id]) {
          result[d.clientProfile.id] = [];
        }

        result[d.clientProfile.id]?.push(d);
      }
    });
  return result;
}

export function getOtherClientMissingDocuments(
  missingDocuments: HouseholdMissingDocumentFragment[]
) {
  const result: Record<string, HouseholdMissingDocumentFragment[]> = {};
  missingDocuments
    .filter((d) => d.clientProfile && !d.clientProfile.isPrimary)
    .forEach((d) => {
      if (d.clientProfile) {
        if (!result[d.clientProfile.id]) {
          result[d.clientProfile.id] = [];
        }
        result[d.clientProfile.id]?.push(d);
      }
    });
  return result;
}

/**
 * Filters out missing documents that are overridden
 */
export function filterNonOverriddenMissingDocuments(
  missingDocuments: HouseholdMissingDocumentFragment[] | null | undefined
) {
  return (missingDocuments ?? []).filter((d) => !d.isOverridden);
}

/**
 * Filters client profiles to include only primary clients or those who are 18 or older
 */
export function filterEligibleClientProfiles<
  T extends { isPrimary?: boolean; dateOfBirth?: Date | null | undefined },
>(
  clientProfiles:
    | { edges?: Array<{ node?: T | null } | null> | null }
    | null
    | undefined
) {
  return (getNodes(clientProfiles) ?? []).filter(
    (cp) => cp.isPrimary || (!!cp.dateOfBirth && is18OrOlder(cp.dateOfBirth))
  );
}
